import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { motion } from 'framer-motion';
import { isEmpty, isNull, isString, isUndefined, merge } from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';

import tableConfiguration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table
 *
 * Reusable table component for displaying tabular data.
 * Handles column visibility, sticky headers, and selection.
 *
 * @param {Object} props
 * @param {string} [props.accessor='default'] - Unique identifier for the table instance.
 * @param {Array} [props.columns=[]] - Array of column definitions.
 * @param {Object} [props.configuration={}] - Additional table configuration.
 * @param {React.ReactNode} [props.headerLeft=null] - Custom header section (left side).
 * @param {string|React.ReactNode} [props.noDataMessage=null] - Message or component to show when no data.
 * @param {Function} [props.onSelection=null] - Callback for row selection.
 * @param {Array} [props.rows=[]] - Array of row data.
 * @param {boolean} [props.stickyHeaders=false] - Whether to pin headers to the top.
 * @returns {React.Element}
 */
export function Table({
  accessor = 'default',
  columns = [],
  configuration = {},
  headerLeft = null,
  noDataMessage = null,
  onSelection = null,
  rows = [],
  stickyHeaders = false,
}) {
  /**
   * Merge default, file, and prop configurations for the table.
   * Ensures initial columns and data are set, and custom header/no-data message is handled.
   */
  const theConfiguration = useMemo(() => {
    const mergedConfiguration = merge(
      {},
      merge(
        {},
        {
          ...tableConfiguration,
          ...{
            renderSubComponent: null,
            uniqueStorageId: `table-${accessor}`,
          },
        },
        configuration
      ),
      {
        initialColumns: columns || [],
        initialData: rows || [],
        noDataMessage:
          isNull(noDataMessage) || isString(noDataMessage) ? (
            <Alert
              css={{
                marginLeft: 'var(--abyss-space-md)',
                marginRight: 'var(--abyss-space-md)',
              }}
              title={noDataMessage || 'No records found.'}
              variant="info"
            />
          ) : (
            noDataMessage
          ),
      }
    );

    if (!isNull(headerLeft)) {
      mergedConfiguration.customHeaderSection = headerLeft;
    }

    if (!isUndefined(configuration?.pageSizeOptions)) {
      mergedConfiguration.pageSizeOptions = configuration?.pageSizeOptions;
    }

    return mergedConfiguration;
  }, [tableConfiguration, configuration, columns, rows, noDataMessage]);

  /**
   * Table state and management from custom hook.
   */
  const tableState = useDataTable(theConfiguration);

  /**
   * Calls onSelection callback whenever selected rows change.
   */
  useLayoutEffect(() => {
    if (onSelection) {
      onSelection(tableState?.state?.selectedFlatRows);
    }
  }, [tableState?.state?.selectedFlatRows]);

  /**
   * Updates table columns and data when rows or columns change.
   */
  useEffect(() => {
    if (tableState?.rows !== rows) {
      tableState?.setColumns(columns, true);
      tableState?.setData(rows, true);
    }
  }, [rows, columns]);

  /**
   * Ensures column visibility matches configuration and default settings.
   * - If a column is visible, sets isHiddenByDefault to false.
   * - If a column should be shown by default but is not visible, ensures it is shown.
   * - Keeps columns with isHiddenByDefault=false always included.
   */
  useEffect(() => {
    const visibleColumnIdSet = new Set(
      tableState?.columnMgmt?.visibleColumns.map((column) => {
        return column.id;
      })
    );

    const updatedColumns = columns.map((column) => {
      return {
        ...column,
        isHiddenByDefault: visibleColumnIdSet.has(column.accessor) ? false : column.isHiddenByDefault,
      };
    });

    const columnsWithVisibilityChange = updatedColumns.filter((updatedColumn) => {
      const originalColumn = tableState?.columnMgmt?.allColumns.find((col) => {
        return col.id === updatedColumn.accessor;
      });
      return originalColumn && originalColumn.isHiddenByDefault !== updatedColumn.isHiddenByDefault;
    });

    if (!isEmpty(columnsWithVisibilityChange)) {
      tableState?.setColumns(updatedColumns, true);
    }
  }, [tableState?.columnMgmt?.visibleColumns, columns]);

  /**
   * Pins table headers to the top of the viewport if stickyHeaders is enabled.
   */
  useLayoutEffect(() => {
    if (stickyHeaders || configuration?.stickyHeaders) {
      const tableHead = document.querySelector('.abyss-table-head');

      if (!isNull(tableHead)) {
        tableHead.setAttribute('style', 'z-index: 300');
        const tableRoot = [...document.querySelectorAll('.abyss-data-table-root')];
        tableRoot.forEach((item) => {
          item.setAttribute('style', `max-height: calc(100vh - ${item.getBoundingClientRect()?.top}px)`);
        });
      }
    }
  }, [stickyHeaders, configuration?.stickyHeaders]);

  return (
    <ErrorHandler location="src/shared/ui/components/TableStatic/Table.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          <DataTable hideTitleHeader key={`table-${accessor}`} tableState={tableState} title="" />
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}
